import React from "react";
import { Box, ListSubheader, MenuItem, Select, Typography } from "@mui/material";
import { useRecoilValue } from "recoil";
import {
  LineChart,
  Line,
  YAxis,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import _ from "underscore";
import useClientNutritionChartData from "../../../../hooks/useClientNutritionChartData";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import useWaterUnit from "../../../../hooks/useWaterUnit";
import useEvents from "../../../../hooks/useEvents";
import NewColors from "../../../../themes/NewColors";
import {
  discoverMicroKeysFromNutritionSeries,
  labelForMicroKey,
} from "../../../../utils/nutritionMicroLabels";

const BUILTIN_ORDER_BASE = [
  { value: "kCal", label: "Calories (kcal)" },
  { value: "water", label: null },
  { value: "fiber", label: "Fiber (g)" },
  { value: "proteins", label: "Protein (g)" },
  { value: "carbs", label: "Carbs (g)" },
  { value: "fats", label: "Fats (g)" },
  { value: "steps", label: "Steps" },
  { value: "averageHeartRate", label: "Heart rate (BPM)" },
  { value: "weight", label: "Weight (lbs)" },
  { value: "sleepHours", label: "Sleep (hrs)" },
];

/** Micronutrient keys that duplicate a built-in series (e.g. fiber in `micronutrients` vs top-level `fiber`). */
const BUILTIN_SERIES_KEYS = new Set(BUILTIN_ORDER_BASE.map((b) => b.value));

function defaultMetricForSeries(data) {
  if (!data || !data.length) return "kCal";
  if (_.some(data, (d) => d.water > 0)) return "water";
  if (_.some(data, (d) => d.fiber > 0)) return "fiber";
  return "kCal";
}

function filterRowsForMetric(chartType, data, events, client) {
  const log = (name) =>
    events.logButtonClick({
      buttonName: name,
      pageName: "Clients",
      tabName: client.uid,
    });

  if (chartType.startsWith("micro:")) {
    const key = chartType.slice(6);
    log(`Micro ${key} Chart Selected`);
    return _.filter(
      data,
      (d) => typeof d.micronutrients?.[key] === "number" && d.micronutrients[key] > 0,
    );
  }

  const map = {
    kCal: () => {
      log("kCal Chart Selected");
      return _.filter(data, (d) => d.kCal > 0);
    },
    proteins: () => {
      log("Proteins Chart Selected");
      return _.filter(data, (d) => d.proteins > 0);
    },
    carbs: () => {
      log("Carbs Chart Selected");
      return _.filter(data, (d) => d.carbs > 0);
    },
    fats: () => {
      log("Fats Chart Selected");
      return _.filter(data, (d) => d.fats > 0);
    },
    weight: () => {
      log("Weight Chart Selected");
      return _.filter(data, (d) => d.weight > 0);
    },
    water: () => {
      log("Water Chart Selected");
      return _.filter(data, (d) => d.water > 0);
    },
    fiber: () => {
      log("Fiber Chart Selected");
      return _.filter(data, (d) => d.fiber > 0);
    },
    sleepHours: () => {
      log("Sleep Chart Selected");
      return _.filter(data, (d) => d.sleepHours > 0);
    },
    steps: () => {
      log("Steps Chart Selected");
      return _.filter(data, (d) => d.steps > 0);
    },
    averageHeartRate: () => {
      log("Heart Rate Chart Selected");
      return _.filter(data, (d) => (d.averageHeartRate || 0) > 0);
    },
  };

  if (map[chartType]) return map[chartType]();
  return data;
}

/** At most this many date labels on the X-axis (avoids overlap on 90-day series). */
const MAX_X_AXIS_TICKS = 6;

function sparseCategoryTicks(lineData, maxTicks = MAX_X_AXIS_TICKS) {
  if (!lineData?.length) return [];
  const n = lineData.length;
  if (n <= maxTicks) {
    return lineData.map((d) => d.createdOnString);
  }
  const idxSet = new Set();
  for (let t = 0; t < maxTicks; t++) {
    idxSet.add(Math.round((t / (maxTicks - 1)) * (n - 1)));
  }
  const ordered = [...idxSet].sort((a, b) => a - b);
  const seen = new Set();
  const out = [];
  for (const i of ordered) {
    const s = lineData[i].createdOnString;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/**
 * @param {object} props
 * @param {object} props.client
 * @param {boolean} [props.useSharedNutritionSeries] — when true, use `nutritionChartData` from parent (single Firestore listener)
 * @param {Array|null} [props.nutritionChartData]
 * @param {boolean} [props.showChartTitle] — when false, hide the metric title above the dropdown (e.g. when the parent already has a section heading)
 */
const NutritionCharts = ({
  client,
  nutritionChartData: sharedSeries,
  useSharedNutritionSeries = false,
  showChartTitle = true,
}) => {
  const organizationId = useRecoilValue(organizationIdSelector);
  const events = useEvents();
  const { unit: waterUnit, formatWater, isImperial: waterIsImperial } = useWaterUnit();
  const BUILTIN_ORDER = React.useMemo(
    () => BUILTIN_ORDER_BASE.map((b) => b.value === "water" ? { ...b, label: `Water (${waterUnit})` } : b),
    [waterUnit],
  );

  const { nutritionChartData: hookSeries } = useClientNutritionChartData({
    uid: client.uid,
    organizationId: organizationId,
    count: 90,
    enabled: !useSharedNutritionSeries,
  });

  const nutritionChartData = useSharedNutritionSeries ? sharedSeries : hookSeries;

  const [chartType, setChartType] = React.useState("water");

  const microKeys = React.useMemo(
    () => discoverMicroKeysFromNutritionSeries(nutritionChartData || []),
    [nutritionChartData],
  );

  const extraMicroKeys = React.useMemo(
    () => microKeys.filter((k) => !BUILTIN_SERIES_KEYS.has(k)),
    [microKeys],
  );

  const pickedDefaultForClient = React.useRef(false);
  React.useEffect(() => {
    pickedDefaultForClient.current = false;
  }, [client?.uid]);

  React.useEffect(() => {
    if (!nutritionChartData?.length) {
      pickedDefaultForClient.current = false;
      return;
    }
    if (!pickedDefaultForClient.current) {
      pickedDefaultForClient.current = true;
      setChartType(defaultMetricForSeries(nutritionChartData));
    }
  }, [nutritionChartData, client?.uid]);

  const chartTitle = React.useMemo(() => {
    if (chartType.startsWith("micro:")) {
      return labelForMicroKey(chartType.slice(6));
    }
    const found = BUILTIN_ORDER.find((b) => b.value === chartType);
    return found ? found.label : chartType;
  }, [chartType]);

  const filteredData = React.useMemo(() => {
    if (!nutritionChartData) return null;
    return filterRowsForMetric(chartType, nutritionChartData, events, client);
  }, [nutritionChartData, chartType, client, events]);

  const lineData = React.useMemo(() => {
    if (!filteredData || !filteredData.length) return [];
    if (chartType.startsWith("micro:")) {
      const key = chartType.slice(6);
      return filteredData.map((d) => ({
        ...d,
        _seriesValue: d.micronutrients?.[key] ?? 0,
      }));
    }
    return filteredData.map((d) => {
      const raw = d[chartType] ?? 0;
      const value = chartType === "water" && waterIsImperial ? formatWater(raw).value : raw;
      return { ...d, _seriesValue: value };
    });
  }, [filteredData, chartType, waterIsImperial, formatWater]);

  const xAxisTicks = React.useMemo(
    () => sparseCategoryTicks(lineData),
    [lineData],
  );

  return (
    <Box
      sx={{
        px: 2,
        pt: showChartTitle ? 2 : 1.5,
        pb: showChartTitle ? 2 : 0.5,
        backgroundColor: NewColors.cardBackground,
        borderRadius: 2,
      }}
    >
      {showChartTitle ? (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {chartTitle}
        </Typography>
      ) : null}
      <Select
        fullWidth
        sx={{ mb: showChartTitle ? 2 : 1 }}
        variant="outlined"
        size="small"
        value={chartType}
        onChange={(event) => {
          setChartType(event.target.value);
        }}
      >
        {BUILTIN_ORDER.slice(0, 6).map((b) => (
          <MenuItem key={b.value} value={b.value}>
            {b.label}
          </MenuItem>
        ))}
        <ListSubheader
          disableSticky
          sx={{ typography: "caption", lineHeight: 2, fontWeight: 700 }}
        >
          Activity &amp; vitals
        </ListSubheader>
        {BUILTIN_ORDER.slice(6).map((b) => (
          <MenuItem key={b.value} value={b.value}>
            {b.label}
          </MenuItem>
        ))}
        {extraMicroKeys.map((k) => (
          <MenuItem key={`micro:${k}`} value={`micro:${k}`}>
            {labelForMicroKey(k)} (g)
          </MenuItem>
        ))}
      </Select>

      {/* Fixed height (no width×aspect). Tight bottom margin reduces empty band under the plot. */}
      <ResponsiveContainer width="100%" height={showChartTitle ? 340 : 300}>
        <LineChart
          data={lineData}
          margin={{ top: 8, right: 10, left: 2, bottom: showChartTitle ? 52 : 36 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="createdOnString"
            type="category"
            ticks={xAxisTicks}
            interval="preserveStartEnd"
            minTickGap={28}
            tick={{ fontSize: 10, fill: "#646472", dy: 6 }}
            angle={-45}
            textAnchor="end"
            height={showChartTitle ? 56 : 44}
            tickMargin={showChartTitle ? 10 : 8}
          />
          <YAxis domain={[0, "auto"]} style={{ fontSize: 11 }} width={44} />
          <Tooltip />
          <Line
            dot={false}
            type="monotone"
            dataKey="_seriesValue"
            name={chartTitle}
            stroke={NewColors.charts.blue}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default NutritionCharts;
